import type { FC } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import * as firstPencilActionCreators from '@makemydeal/dr-dash-store/actions/firstPencilActionCreators';
import * as firstPencilSelectors from '@makemydeal/dr-dash-store/selectors/firstPencilSelectors';
import type { FirstPencilIncentiveOverride, FirstPencilManualIncentive } from '@makemydeal/dr-dash-store/types/firstPencilTypes';
import type { ManualIncentiveEditEntry, ManualIncentiveEditType } from '@makemydeal/dr-dash-store/types/manualIncentiveTypes';
import { INCENTIVE_TYPE_VALUES } from '@makemydeal/dr-dash-types';

import { generateEditId } from '../../utils/rebatesUtils';
import { ADD_INCENTIVE_BUTTON, INCENTIVE_FIELD_NAMES, INCENTIVES } from '../../constants';
import { ManualRebatesFormContainer, AddIncentiveContainer, RebatesEditWrapper } from '../rebates/Rebates.style';
import { ManualRebatesFormHeader } from '../rebates/ManualRebatesFormHeader';
import { RebatesTableRow } from '../rebates/RebatesTableRow';
import { hasDuplicateManualIncentiveCode, toIncentiveSelection, toggleIncentiveSelection } from './utils';
import { useSelectedProgramIncentiveRows } from './useFirstPencilIncentiveRows';
import type { FirstPencilIncentiveRow, FirstPencilProgramsOfferType } from './types';

type IncentiveRowEntry =
    | { kind: 'program'; key: string; entry: ManualIncentiveEditEntry; row: FirstPencilIncentiveRow }
    | { kind: 'manual'; key: string; entry: ManualIncentiveEditEntry; incentive: FirstPencilManualIncentive };

const createManualIncentive = (): FirstPencilManualIncentive => ({
    editId: generateEditId(),
    program: '',
    name: '',
    code: undefined,
    amount: 0,
    type: INCENTIVE_TYPE_VALUES.customerCash.value,
    selected: true,
    deleted: false
});

const toOverride = (
    row: FirstPencilIncentiveRow,
    current: FirstPencilIncentiveOverride | undefined
): FirstPencilIncentiveOverride => ({
    incentiveId: row.incentiveId,
    programId: row.programId,
    programName: row.programName,
    incentiveType: row.incentiveType,
    originalAmount: row.cash,
    program: current?.program ?? row.programId,
    name: current?.name ?? row.programName,
    amount: current?.amount ?? row.cash
});

const toProgramEntry = (row: FirstPencilIncentiveRow, override: FirstPencilIncentiveOverride): ManualIncentiveEditEntry => ({
    program: override.program,
    name: override.name,
    description: row.description,
    code: row.incentiveId,
    type: row.incentiveType as ManualIncentiveEditType,
    amount: override.amount,
    originalAmount: row.cash,
    manual: false,
    isStandardIncentive: !row.isConditional,
    deleted: false
});

const toManualEntry = ({ program, name, code, amount, type, deleted }: FirstPencilManualIncentive): ManualIncentiveEditEntry => ({
    program,
    name,
    description: '',
    code,
    type: type as ManualIncentiveEditType,
    amount,
    originalAmount: amount,
    manual: true,
    isStandardIncentive: false,
    deleted
});

const toNumber = (value: string | number): number => Number(value) || 0;

type FirstPencilManualIncentivesProps = {
    offerType: FirstPencilProgramsOfferType;
};

export const FirstPencilManualIncentives: FC<FirstPencilManualIncentivesProps> = ({ offerType }) => {
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const isRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const isProgramsLoading = useSelector(firstPencilSelectors.isProgramsLoading);
    const manualIncentives = useSelector(firstPencilSelectors.getManualIncentives);
    const overrides = useSelector(firstPencilSelectors.getIncentiveOverrides);
    const selectedProgram = useSelector(firstPencilSelectors.getSelectedProgram);
    const selectedIncentives = useSelector(firstPencilSelectors.getSelectedIncentives);
    const programRows = useSelectedProgramIncentiveRows(offerType);
    const codeCellErrorIndexes = useRef(new Set<number>()).current;
    const term = selectedProgram?.offerType === offerType ? (selectedProgram.program.term as number | undefined) : undefined;

    const rows = useMemo<IncentiveRowEntry[]>(
        () => [
            ...programRows
                .filter((row) => row.selected)
                .map((row) => ({
                    kind: 'program' as const,
                    key: `program-${row.incentiveId}`,
                    entry: toProgramEntry(row, toOverride(row, overrides?.[row.incentiveId])),
                    row
                })),
            ...manualIncentives.map((incentive) => ({
                kind: 'manual' as const,
                key: incentive.editId,
                entry: toManualEntry(incentive),
                incentive
            }))
        ],
        [programRows, overrides, manualIncentives]
    );

    const handleInputChange = useCallback(
        (value: string | number, fieldName: keyof ManualIncentiveEditEntry, index: number) => {
            const target = rows[index];
            if (target.kind === 'program') {
                const current = toOverride(target.row, overrides?.[target.row.incentiveId]);
                const next = {
                    ...current,
                    ...(fieldName === INCENTIVE_FIELD_NAMES.PROGRAM && { program: String(value) }),
                    ...(fieldName === INCENTIVE_FIELD_NAMES.NAME && { name: String(value) }),
                    ...(fieldName === INCENTIVE_FIELD_NAMES.AMOUNT && { amount: toNumber(value) })
                };
                if (next.program !== current.program || next.name !== current.name || next.amount !== current.amount) {
                    if (!selectedIncentives) {
                        dispatch(firstPencilActionCreators.incentivesSelected(toIncentiveSelection(programRows)));
                    }
                    dispatch(firstPencilActionCreators.incentiveOverridden(next));
                }
                return;
            }
            const changes: Partial<Omit<FirstPencilManualIncentive, 'editId'>> =
                fieldName === INCENTIVE_FIELD_NAMES.CODE
                    ? { code: value === '' ? undefined : toNumber(value) }
                    : fieldName === INCENTIVE_FIELD_NAMES.AMOUNT
                    ? { amount: toNumber(value) }
                    : { [fieldName]: String(value) };
            dispatch(firstPencilActionCreators.manualIncentiveUpdated(target.incentive.editId, changes));
        },
        [dispatch, overrides, programRows, rows, selectedIncentives]
    );

    const handleDeleteOrUndo = useCallback(
        (index: number) => {
            const target = rows[index];
            if (target.kind === 'program') {
                if (isProgramsLoading || (!isRestorationEnabled && !target.row.isConditional)) {
                    return;
                }
                dispatch(firstPencilActionCreators.incentivesSelected(toggleIncentiveSelection(programRows, target.row, false)));
                return;
            }
            dispatch(
                firstPencilActionCreators.manualIncentiveUpdated(target.incentive.editId, { deleted: !target.incentive.deleted })
            );
        },
        [dispatch, isProgramsLoading, isRestorationEnabled, programRows, rows]
    );

    const handleAdd = useCallback(() => {
        dispatch(firstPencilActionCreators.manualIncentiveAdded(createManualIncentive()));
    }, [dispatch]);

    return (
        <>
            <Typography variant="h3">{INCENTIVES}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <RebatesEditWrapper data-testid="first-pencil-manual-incentives">
                    <ManualRebatesFormContainer id="manual-rebates-form-container">
                        <ManualRebatesFormHeader />
                        {rows.map((target, index) => (
                            <RebatesTableRow
                                key={target.key}
                                term={term}
                                incentive={target.entry}
                                incentiveIndex={index}
                                isEditing
                                codeCellErrorIndexes={codeCellErrorIndexes}
                                isUniqueCode={
                                    target.kind === 'program' ||
                                    !hasDuplicateManualIncentiveCode(target.incentive, manualIncentives)
                                }
                                handleIncentiveDeleteOrUndo={handleDeleteOrUndo}
                                handleInputChange={handleInputChange}
                            />
                        ))}
                    </ManualRebatesFormContainer>
                    <AddIncentiveContainer>
                        <Button
                            buttonStyle="secondary"
                            data-testid="first-pencil-add-manual-incentive"
                            onClick={handleAdd}
                            size="small"
                            disabled={isLocked}
                        >
                            {ADD_INCENTIVE_BUTTON}
                        </Button>
                    </AddIncentiveContainer>
                </RebatesEditWrapper>
            </Box>
        </>
    );
};
